import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  AlertCircle, ArchiveRestore, CheckCircle2, Clock, FileSpreadsheet, History, Loader2, RefreshCcw, RotateCcw,
  Search, ShieldCheck, Trash2, Undo2, X, XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { EmptyState, KpiCard, PageHeader, Spinner } from "../../components/common";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader } from "../../components/ui/card";
import { Checkbox } from "../../components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../components/ui/tooltip";
import { PaginationBar, TableRowsSkeleton, useDebounced } from "../../components/PaginationBar";
import { BatchLogSelectionBar, DeleteBatchLogsDialog } from "../../components/DeleteBatchLogsDialog";
import { api, errMsg, fmtDateIST, fmtTimeIST } from "../../lib/api";
import { KIND_LABELS, logKey, parseLogKey } from "../../lib/batchLogs";

/**
 * Phase 151 — Super Admin › Batch Logs.
 * Every batch history record of a municipality (Bulk Excel Upload, Bulk Entry, Bulk Update, Import Payments —
 * main + Third Party) with checkboxes: Delete Selected (soft-delete of the log entry ONLY — imported data is never
 * touched) and, under the Deleted tab, Restore Selected. Also holds the per-municipality switch that lets the
 * Municipal Admin delete batch logs from Demand Management.
 */
const KINDS = [["all", "All"], ["excel", "Bulk Excel Upload"], ["bulk_entry", "Bulk Entry"], ["bulk_update", "Bulk Update"], ["payment_import", "Import Payments"]];
const KIND_TONE = {
  excel: "bg-primary/10 text-primary border-primary/20",
  bulk_entry: "bg-teal-50 text-teal-700 border-teal-200",
  bulk_update: "bg-violet-50 text-violet-800 border-violet-200",
  payment_import: "bg-amber-50 text-amber-800 border-amber-200",
};
const STATUS_TONE = {
  "Completed": { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: CheckCircle2 },
  "Rolled back": { cls: "bg-violet-50 text-violet-800 border-violet-200", Icon: Undo2 },
  "Failed": { cls: "bg-red-50 text-red-700 border-red-200", Icon: XCircle },
  "Stopped": { cls: "bg-amber-50 text-amber-800 border-amber-200", Icon: XCircle },
  "Processing": { cls: "bg-primary/10 text-primary border-primary/20", Icon: Loader2 },
  "Applying": { cls: "bg-primary/10 text-primary border-primary/20", Icon: Loader2 },
  "Not processed": { cls: "bg-slate-100 text-slate-600 border-slate-200", Icon: Clock },
};
const fmtNum = (n) => (n || 0).toLocaleString("en-IN");

const StatusChip = ({ label }) => {
  const m = STATUS_TONE[label] || { cls: "bg-slate-100 text-slate-600 border-slate-200", Icon: Clock };
  const Icon = m.Icon;
  return (
    <Badge variant="outline" className={`text-[10px] gap-1 ${m.cls}`}>
      <Icon className={`h-2.5 w-2.5 ${label === "Processing" || label === "Applying" ? "animate-spin" : ""}`} /> {label}
    </Badge>
  );
};

export default function BatchLogs() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [munis, setMunis] = useState(null);
  const code = searchParams.get("muni") || "";
  const [tab, setTab] = useState("active");          // active | deleted
  const [kind, setKind] = useState("all");
  const [q, setQ] = useState("");
  const search = useDebounced(q);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [permBusy, setPermBusy] = useState(false);

  useEffect(() => {
    api.get("/platform/municipalities")
      .then((r) => setMunis((r.data || []).filter((m) => m.is_active !== false)))
      .catch((e) => { toast.error(errMsg(e)); setMunis([]); });
  }, []);
  const muni = useMemo(() => (munis || []).find((m) => m.code === code) || null, [munis, code]);

  const load = useCallback(() => {
    if (!code) return undefined;
    setLoading(true);
    setError(null);
    return api.get(`/platform/municipalities/${code}/batch-logs`,
      { params: { status: tab, kind: kind === "all" ? undefined : kind, search: search || undefined, page, limit } })
      .then((r) => setData(r.data))
      .catch((e) => { setError(errMsg(e)); })
      .finally(() => setLoading(false));
  }, [code, tab, kind, search, page, limit]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSelected(new Set()); setPage(1); }, [code, tab, kind, search]);
  useEffect(() => { setSelected(new Set()); }, [page, limit]);

  const selectMuni = (c) => { setSearchParams(c ? { muni: c } : {}); setData(null); };

  const items = data?.items || [];
  const selectable = tab === "active" ? items.filter((b) => b.deletable) : items;
  const allSelected = selectable.length > 0 && selectable.every((b) => selected.has(logKey(b.kind, b.id)));
  const someSelected = selectable.some((b) => selected.has(logKey(b.kind, b.id)));
  const toggleAll = (on) => setSelected((prev) => { const n = new Set(prev); selectable.forEach((b) => { const k = logKey(b.kind, b.id); if (on) n.add(k); else n.delete(k); }); return n; });
  const toggleOne = (b, on) => setSelected((prev) => { const n = new Set(prev); const k = logKey(b.kind, b.id); if (on) n.add(k); else n.delete(k); return n; });
  const selectedItems = [...selected].map((k) => { const { kind: kd, id } = parseLogKey(k); return { kind: kd, id, filename: items.find((x) => x.kind === kd && x.id === id)?.filename }; });

  const doDelete = (its) => api.post(`/platform/municipalities/${code}/batch-logs/delete`, { items: its }).then((r) => r.data);

  const doRestore = async () => {
    setRestoring(true);
    try {
      const r = await api.post(`/platform/municipalities/${code}/batch-logs/restore`, { items: selectedItems.map(({ kind: kd, id }) => ({ kind: kd, id })) });
      toast.success(`${fmtNum(r.data.restored)} batch record${r.data.restored === 1 ? "" : "s"} restored`,
        { description: "They are visible again in the municipality's Batch History lists" });
      setRestoreOpen(false); setSelected(new Set()); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setRestoring(false); }
  };

  const setPermission = async (on) => {
    setPermBusy(true);
    try {
      await api.put(`/platform/municipalities/${code}/batch-logs/permission`, { allow_admin_delete: on });
      setData((d) => (d ? { ...d, allow_admin_delete: on } : d));
      toast.success(on ? "Municipal Admin can now delete batch history records" : "Municipal Admin can no longer delete batch history records",
        { description: on ? "Checkboxes and “Delete Selected” appear on the municipality's Demand Management batch lists." : "Only you (Super Admin) can remove batch logs for this municipality." });
    } catch (e) { toast.error(errMsg(e)); } finally { setPermBusy(false); }
  };

  const deletedTotal = data?.deleted_counts?.total || 0;

  return (
    <div className="space-y-5 pb-24" data-testid="super-batch-logs-page">
      <PageHeader title="Batch Logs"
        subtitle="Clean up a municipality's batch history — Bulk Excel Upload, Bulk Entry, Bulk Update and Import Payments records. Deleting a record removes only the history entry; imported data is never touched.">
        <Button variant="outline" size="sm" onClick={load} disabled={!code || loading} data-testid="batch-logs-refresh-button">
          <RefreshCcw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </PageHeader>

      {/* ---- municipality + permission ---- */}
      <Card className="shadow-sm">
        <CardContent className="p-4 sm:p-5 flex flex-col lg:flex-row lg:items-end gap-4">
          <div className="space-y-1.5 flex-1 max-w-md">
            <Label>Municipality</Label>
            {!munis ? <Spinner label="Loading municipalities..." /> : (
              <Select value={code} onValueChange={selectMuni}>
                <SelectTrigger data-testid="batch-logs-muni-select"><SelectValue placeholder="Select a municipality" /></SelectTrigger>
                <SelectContent>
                  {munis.map((m) => (
                    <SelectItem key={m.code} value={m.code} data-testid={`batch-logs-muni-option-${m.code}`}>
                      {m.name} <span className="text-muted-foreground font-mono text-xs">({m.code})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          {code && (
            <div className="flex items-start gap-3 rounded-md border bg-muted/30 px-3 py-2.5 flex-1" data-testid="batch-logs-permission-card">
              <Switch checked={!!data?.allow_admin_delete} onCheckedChange={setPermission} disabled={permBusy || !data}
                aria-label="Allow Municipal Admin to delete batch logs" data-testid="batch-logs-permission-switch" />
              <div className="min-w-0">
                <p className="text-sm font-medium leading-tight">Allow Municipal Admin to delete batch logs</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  {data?.allow_admin_delete
                    ? "ON — the Municipal Admin sees checkboxes and “Delete Selected” on every Batch History list in Demand Management."
                    : "OFF (default) — only you can remove this municipality's batch records, from this page."}
                </p>
              </div>
            </div>
          )}
          <div className="text-xs bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] rounded-md px-3 py-2 flex gap-2 flex-1" data-testid="batch-logs-note">
            <ShieldCheck className="h-4 w-4 shrink-0 mt-0.5" />
            <span>
              Removing a batch record is a <span className="font-semibold">history clean-up only</span>. Properties, demands,
              payments and receipts created by the batch are never rolled back or changed, and deleted records can be restored here.
            </span>
          </div>
        </CardContent>
      </Card>

      {!code ? (
        <Card className="shadow-sm"><CardContent className="p-0">
          <EmptyState title="Pick a municipality to begin" subtitle="You'll see every batch history record it has, with checkboxes to remove or restore them." />
        </CardContent></Card>
      ) : (
        <>
          {/* ---- KPI tiles ---- */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard label={tab === "active" ? "Batch records" : "Deleted records"} value={data ? fmtNum(data.total) : "—"} icon={History} testId="batch-logs-count-total" />
            <KpiCard label="Removable now" value={data ? fmtNum(tab === "active" ? data.deletable : data.total) : "—"} icon={Trash2}
              sub="Finished batches only — processing ones wait" testId="batch-logs-count-deletable" />
            <KpiCard label="In deleted bin" value={data ? fmtNum(deletedTotal) : "—"} icon={ArchiveRestore} sub="Restorable any time" testId="batch-logs-count-deleted" />
            <KpiCard label="Import Payments" value={data ? fmtNum(data.counts_by_kind?.payment_import) : "—"} icon={FileSpreadsheet}
              sub={data ? `${fmtNum(data.counts_by_kind?.excel)} Excel · ${fmtNum(data.counts_by_kind?.bulk_entry)} Entry · ${fmtNum(data.counts_by_kind?.bulk_update)} Update` : undefined}
              testId="batch-logs-count-kinds" />
          </div>

          {/* ---- filter bar ---- */}
          <Card className="shadow-sm">
            <CardContent className="p-3 sm:p-4 flex flex-wrap gap-2 items-center">
              <Tabs value={tab} onValueChange={setTab}>
                <TabsList>
                  <TabsTrigger value="active" data-testid="batch-logs-tab-active">Active</TabsTrigger>
                  <TabsTrigger value="deleted" data-testid="batch-logs-tab-deleted">
                    Deleted {deletedTotal > 0 && <Badge variant="secondary" className="ml-1.5 text-[10px]">{fmtNum(deletedTotal)}</Badge>}
                  </TabsTrigger>
                </TabsList>
              </Tabs>
              <div className="flex flex-wrap gap-1.5" data-testid="batch-logs-kind-filter">
                {KINDS.map(([v, l]) => (
                  <button key={v} type="button" onClick={() => setKind(v)} aria-pressed={kind === v}
                    className={`text-xs px-2.5 py-1 rounded-full border transition-colors inline-flex items-center gap-1.5 ${kind === v
                      ? "bg-primary text-primary-foreground border-primary" : "bg-background text-muted-foreground hover:bg-muted"}`}
                    data-testid={`batch-logs-kind-${v}`}>
                    {l}
                    {data?.counts_by_kind && v !== "all" && kind === "all" && (
                      <span className="tabular-nums text-[10px] font-semibold rounded-full px-1.5 py-px bg-muted">{fmtNum(data.counts_by_kind[v])}</span>
                    )}
                  </button>
                ))}
              </div>
              <div className="relative flex-1 min-w-[200px]">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input className="pl-9 h-9" placeholder="Search file name or uploaded by" value={q} onChange={(e) => setQ(e.target.value)} data-testid="batch-logs-search-input" />
              </div>
              {(q || kind !== "all") && (
                <Button variant="ghost" size="sm" className="h-9" onClick={() => { setQ(""); setKind("all"); }} data-testid="batch-logs-reset-filters-button">
                  <X className="h-3.5 w-3.5 mr-1" /> Reset
                </Button>
              )}
            </CardContent>
          </Card>

          {/* ---- table ---- */}
          <Card className="shadow-sm">
            <CardHeader className="py-3 px-4 flex-row items-center justify-between space-y-0 gap-2 flex-wrap border-b">
              <p className="text-xs text-muted-foreground tabular-nums" data-testid="batch-logs-total">{data ? `${fmtNum(data.total)} record${data.total === 1 ? "" : "s"}` : "—"}</p>
              {tab === "active" ? (
                <BatchLogSelectionBar count={selected.size} onClear={() => setSelected(new Set())} onDelete={() => setDeleteOpen(true)} disabled={loading} testId="batch-logs" />
              ) : (
                <div className="flex items-center gap-2 flex-wrap" data-testid="batch-logs-restore-bar">
                  {selected.size > 0 && (
                    <>
                      <span className="text-xs font-medium tabular-nums" data-testid="batch-logs-restore-selected-count">{fmtNum(selected.size)} selected</span>
                      <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setSelected(new Set())} data-testid="batch-logs-restore-clear-selection">Clear</Button>
                    </>
                  )}
                  <Button size="sm" className="h-7" onClick={() => setRestoreOpen(true)} disabled={loading || selected.size === 0} data-testid="batch-logs-restore-selected">
                    <RotateCcw className="h-3.5 w-3.5 mr-1" /> Restore Selected{selected.size > 0 ? ` (${fmtNum(selected.size)})` : ""}
                  </Button>
                </div>
              )}
            </CardHeader>
            <CardContent className="p-0">
              {error && !data ? (
                <div className="p-6 text-sm text-center space-y-2" data-testid="batch-logs-error">
                  <AlertCircle className="h-5 w-5 mx-auto text-destructive" />
                  <p className="text-destructive">{error}</p>
                  <Button variant="outline" size="sm" onClick={load}>Retry</Button>
                </div>
              ) : !data ? <TableRowsSkeleton rows={6} cols={8} testId="batch-logs-skeleton" /> : items.length === 0 ? (
                <div className="p-8 text-center" data-testid="batch-logs-empty">
                  <FileSpreadsheet className="h-8 w-8 mx-auto text-muted-foreground/60" />
                  <p className="text-sm font-medium mt-2">{tab === "deleted" ? "No deleted batch records" : q || kind !== "all" ? "No batch records match" : "No batch records yet"}</p>
                  <p className="text-xs text-muted-foreground">{tab === "deleted" ? "Records you remove from the Active tab land here and can be restored." : "Upload / import batches of this municipality appear here."}</p>
                </div>
              ) : (
                <TooltipProvider delayDuration={200}>
                  <div className={`overflow-x-auto ${loading ? "opacity-60 transition-opacity" : ""}`}>
                    <Table data-testid="batch-logs-table">
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-10">
                            <Checkbox checked={allSelected ? true : someSelected ? "indeterminate" : false} onCheckedChange={(on) => toggleAll(on === true)}
                              disabled={selectable.length === 0} aria-label="Select all batches on this page" data-testid="batch-logs-select-all" />
                          </TableHead>
                          <TableHead>Date / Time</TableHead>
                          <TableHead>Type / File</TableHead>
                          <TableHead>Uploaded by</TableHead>
                          <TableHead className="text-center">Total rows</TableHead>
                          <TableHead className="text-center">Successful</TableHead>
                          <TableHead className="text-center">Failed</TableHead>
                          <TableHead>Status</TableHead>
                          {tab === "deleted" && <TableHead>Deleted</TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {items.map((b) => {
                          const k = logKey(b.kind, b.id);
                          const isSel = selected.has(k);
                          const can = tab === "deleted" || b.deletable;
                          return (
                            <TableRow key={k} className={isSel ? "bg-primary/5" : b.undone ? "bg-muted/30" : ""} data-state={isSel ? "selected" : undefined}
                              data-testid={`batch-logs-row-${b.id}`} data-kind={b.kind}>
                              <TableCell className="w-10">
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="inline-flex" tabIndex={can ? -1 : 0}>
                                      <Checkbox checked={isSel} onCheckedChange={(on) => toggleOne(b, on === true)} disabled={!can}
                                        aria-label={`Select batch ${b.filename || b.id}`} data-testid={`batch-logs-select-${b.id}`} />
                                    </span>
                                  </TooltipTrigger>
                                  {!can && <TooltipContent side="right" className="text-xs max-w-[240px]">{b.reason_text || "Cannot be removed right now"}</TooltipContent>}
                                </Tooltip>
                              </TableCell>
                              <TableCell className="text-xs whitespace-nowrap">
                                <p data-testid={`batch-logs-date-${b.id}`}>{fmtDateIST(b.created_at)}</p>
                                <p className="text-muted-foreground tabular-nums">{fmtTimeIST(b.created_at)} IST</p>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <Badge variant="outline" className={`text-[10px] ${KIND_TONE[b.kind] || ""}`} data-testid={`batch-logs-kind-badge-${b.id}`}>{b.kind_label}</Badge>
                                  {b.kind === "payment_import" && <Badge variant="outline" className="text-[10px] font-normal">{b.scope === "third_party" ? "Third Party" : "Main register"}</Badge>}
                                </div>
                                <p className="text-xs truncate max-w-[240px] mt-0.5" title={b.filename} data-testid={`batch-logs-file-${b.id}`}>{b.filename || "-"}</p>
                                {b.fy_label && <span className="text-[10px] text-muted-foreground">{b.fy_label}</span>}
                              </TableCell>
                              <TableCell className="text-xs max-w-[160px] truncate" title={b.by}>{b.by || "-"}</TableCell>
                              <TableCell className="text-center text-sm tabular-nums">{b.total_rows ?? "-"}</TableCell>
                              <TableCell className="text-center text-sm tabular-nums font-medium text-emerald-700">{b.success ?? "-"}</TableCell>
                              <TableCell className={`text-center text-sm tabular-nums font-medium ${b.failed ? "text-red-700" : "text-muted-foreground"}`}>{b.failed ?? "-"}</TableCell>
                              <TableCell><StatusChip label={b.status_label} /></TableCell>
                              {tab === "deleted" && (
                                <TableCell className="text-xs whitespace-nowrap" data-testid={`batch-logs-deleted-at-${b.id}`}>
                                  <p>{fmtDateIST(b.log_deleted_at)} {fmtTimeIST(b.log_deleted_at)}</p>
                                  <p className="text-muted-foreground truncate max-w-[160px]">{b.log_deleted_by?.name || "—"}{b.log_deleted_by?.role === "super_admin" ? " · Super Admin" : b.log_deleted_by?.role === "municipal_admin" ? " · Municipal Admin" : ""}</p>
                                </TableCell>
                              )}
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                  {data.total > 0 && (
                    <div className="px-4 pb-3 border-t">
                      <PaginationBar page={page} limit={limit} total={data.total} loading={loading}
                        onPageChange={setPage} onLimitChange={(n) => { setLimit(n); setPage(1); }} testId="batch-logs-pagination" />
                    </div>
                  )}
                </TooltipProvider>
              )}
            </CardContent>
          </Card>
        </>
      )}

      <DeleteBatchLogsDialog open={deleteOpen} onOpenChange={setDeleteOpen} items={selectedItems} request={doDelete}
        onDone={() => { setSelected(new Set()); load(); }} testId="batch-logs-delete" />

      <Dialog open={restoreOpen} onOpenChange={(v) => !restoring && setRestoreOpen(v)}>
        <DialogContent className="sm:max-w-md" data-testid="batch-logs-restore-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading"><RotateCcw className="h-5 w-5 text-primary" /> Restore {fmtNum(selected.size)} batch record{selected.size === 1 ? "" : "s"}?</DialogTitle>
            <DialogDescription>They will reappear in {muni?.name || "the municipality"}'s Batch History lists exactly as before. No data changes.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRestoreOpen(false)} disabled={restoring} data-testid="batch-logs-restore-cancel">Cancel</Button>
            <Button onClick={doRestore} disabled={restoring} data-testid="batch-logs-restore-confirm">
              {restoring ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-1.5" />} {restoring ? "Restoring…" : "Restore"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
